/**
 * <bundle-stack>
 *
 * Visually stacks 2+ bundle cards into a layered pile (top card fully visible,
 * subsequent cards peeking out below). Tap anywhere on the stack except the
 * top "Bundle" CTA to expand. After expansion, collapses once the viewport has
 * scrolled down at least --bundle-stack-collapse-scroll-px (default 600px) from
 * the scroll position captured when the listener attached.
 *
 * For a single card, just renders inline with no interactive stack behavior.
 */
const BUNDLE_STACK_SCALE_STEP = 0.05;

class BundleStack extends HTMLElement {
  constructor() {
    super();
    this.expanded = false;
    this._scrollAtExpand = 0;
    this._collapseScrollThreshold = 600;
    this._zone = null;
    this._viewAllWrap = null;
    this._toggleButton = null;
    this._onClick = this._onClick.bind(this);
    this._onToggleButtonClick = this._onToggleButtonClick.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onCollapseEnd = this._onCollapseEnd.bind(this);
    this._onExpandEnd = this._onExpandEnd.bind(this);
    this._scheduleMeasure = this._scheduleMeasure.bind(this);
    this._measure = this._measure.bind(this);
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll(':scope > .bundle-card'));
    if (this.cards.length === 0) {
      queueMicrotask(() => {
        this.cards = Array.from(this.querySelectorAll(':scope > .bundle-card'));
        if (this.cards.length === 0) return;
        this._initCards();
      });
      return;
    }
    this._initCards();
  }

  _initCards() {
    this.cards.forEach((card, i) => {
      card.style.setProperty('--stack-index', i);
      card.setAttribute('data-stack-index', String(i));
    });

    this._measure();
    this.classList.add('is-ready');
    this._initLayoutWrapper();
    this._cacheZoneFootprints();
    this._measureZoneHeights();
    this._syncZoneExpandedClass();
    this._initToggleButton();
    this._bindMeasureTriggers();

    if (this.cards.length < 2) {
      this.classList.add('bundle-stack--single');
      return;
    }

    this.classList.add('bundle-stack--multi');
    this.addEventListener('click', this._onClick);
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  _initLayoutWrapper() {
    if (this.cards.length < 2) return;

    const viewAll = this.nextElementSibling;
    if (!viewAll?.classList.contains('bundle-stack__view-all')) return;

    const viewAllWrap = document.createElement('div');
    viewAllWrap.className = 'bundle-stack__view-all-wrap';
    viewAll.parentNode.insertBefore(viewAllWrap, viewAll);
    viewAllWrap.appendChild(viewAll);
    this._viewAllWrap = viewAllWrap;

    const zone = document.createElement('div');
    zone.className = 'bundle-stack-zone';
    this.parentNode.insertBefore(zone, this);
    zone.appendChild(this);
    zone.appendChild(viewAllWrap);
    this._zone = zone;
  }

  _bindMeasureTriggers() {
    requestAnimationFrame(() => this._measure());

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._measure());
    }

    this.cards.forEach((card) => {
      card.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', this._scheduleMeasure, { once: true });
        }
      });
    });

    if (!('ResizeObserver' in window)) return;

    this._ro = new ResizeObserver(this._scheduleMeasure);
    this.cards.forEach((card) => {
      this._ro.observe(card);
      const layout = card.querySelector('.bundle-card__layout');
      if (layout) this._ro.observe(layout);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this._toggleButton) {
      this._toggleButton.removeEventListener('click', this._onToggleButtonClick);
      this._toggleButton = null;
    }
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._ro) this._ro.disconnect();
    if (this._scrollListenTimer) clearTimeout(this._scrollListenTimer);
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    if (this._measureRaf) cancelAnimationFrame(this._measureRaf);
    this._clearCollapseAnimation();
  }

  _initToggleButton() {
    const viewAll = this._viewAllWrap?.querySelector('.bundle-stack__view-all')
      ?? this.nextElementSibling;
    if (!viewAll?.classList.contains('bundle-stack__view-all')) return;

    this._toggleButton = viewAll;
    this._toggleButton.addEventListener('click', this._onToggleButtonClick);
  }

  _isAnimating() {
    return this.classList.contains('is-collapsing')
      || this.classList.contains('is-expanding');
  }

  _scheduleMeasure() {
    if (this._isAnimating()) return;
    if (this._measureRaf) cancelAnimationFrame(this._measureRaf);
    this._measureRaf = requestAnimationFrame(() => {
      this._measureRaf = null;
      this._measure();
    });
  }

  _onToggleButtonClick(e) {
    e.preventDefault();
    if (!this.expanded) this._expand();
  }

  _collapsedCardScale(index) {
    return index === 0 ? 1 : 1 - index * BUNDLE_STACK_SCALE_STEP;
  }

  _collapsedStackHeight(shellHeight, cardCount, peek) {
    const lastIndex = Math.max(0, cardCount - 1);
    if (lastIndex === 0) return shellHeight;
    return lastIndex * peek + shellHeight * this._collapsedCardScale(lastIndex);
  }

  _stackVars() {
    const styles = getComputedStyle(this);
    return {
      peek: parseFloat(styles.getPropertyValue('--bundle-stack-peek')) || 10,
      gap: parseFloat(styles.getPropertyValue('--bundle-stack-gap')) || 8,
      shellHeight: parseFloat(styles.getPropertyValue('--stack-shell-height')) || 0,
      collapsedHeight: parseFloat(styles.getPropertyValue('--collapsed-height')) || 0,
      expandedHeight: parseFloat(styles.getPropertyValue('--expanded-height')) || 0,
    };
  }

  _measure() {
    if (!this.cards || this.cards.length === 0) return;

    const wasExpanded = this.classList.contains('is-expanded');
    const wasCollapsing = this.classList.contains('is-collapsing');
    const styles = getComputedStyle(this);
    const peek = parseFloat(styles.getPropertyValue('--bundle-stack-peek')) || 10;
    const gap = parseFloat(styles.getPropertyValue('--bundle-stack-gap')) || 8;

    this.classList.add('is-measuring');

    this.classList.add('is-measuring--shell');
    const shellHeight = this.cards[0]?.offsetHeight || 0;
    const collapsedHeight = this._collapsedStackHeight(shellHeight, this.cards.length, peek);

    this.style.setProperty('--stack-shell-height', `${shellHeight}px`);
    this.style.setProperty('--collapsed-height', `${collapsedHeight}px`);

    this.cards.forEach((card, i) => {
      card.style.setProperty('--collapsed-top', `${i * peek}px`);
    });

    this.classList.remove('is-measuring--shell');
    this.classList.add('is-measuring--expanded');
    const expandedHeights = this.cards.map((c) => c.offsetHeight);

    let expandedHeight = 0;
    expandedHeights.forEach((h, i) => {
      expandedHeight += h;
      if (i < expandedHeights.length - 1) expandedHeight += gap;
    });

    this.cards.forEach((card, i) => {
      let expandedTop = 0;
      for (let j = 0; j < i; j += 1) expandedTop += expandedHeights[j] + gap;
      card.style.setProperty('--expanded-top', `${expandedTop}px`);
      card.style.setProperty('--card-expanded-height', `${expandedHeights[i]}px`);
    });

    this.style.setProperty('--expanded-height', `${expandedHeight}px`);

    this.classList.remove('is-measuring', 'is-measuring--expanded');
    if (wasExpanded) this.classList.add('is-expanded');
    if (wasCollapsing) this.classList.add('is-collapsing');
    this._measureZoneHeights();
    this._syncZoneExpandedClass();
    if (wasExpanded && this._zone && !this._isAnimating()) {
      this._lockZoneToNaturalHeight();
    }
  }

  _cacheZoneFootprints() {
    if (!this._zone || !this._viewAllWrap) return;

    const isExpanded = this._zone.classList.contains('bundle-stack-zone--expanded');

    if (!isExpanded) {
      this._zone.style.setProperty('--view-all-footprint', `${this._viewAllWrap.offsetHeight}px`);
    }

    if (isExpanded) {
      this._zone.style.setProperty(
        '--zone-expanded-padding',
        `${parseFloat(getComputedStyle(this._zone).paddingBottom) || 0}px`
      );
      return;
    }

    const cachedPadding = getComputedStyle(this._zone).getPropertyValue('--zone-expanded-padding').trim();
    if (cachedPadding) return;

    this._zone.classList.add('bundle-stack-zone--expanded');
    this._zone.style.setProperty(
      '--zone-expanded-padding',
      `${parseFloat(getComputedStyle(this._zone).paddingBottom) || 0}px`
    );
    this._zone.classList.remove('bundle-stack-zone--expanded');
  }

  _measureZoneHeights() {
    if (!this._zone || !this._viewAllWrap) return;

    const styles = getComputedStyle(this);
    const collapsedStack = parseFloat(styles.getPropertyValue('--collapsed-height')) || 0;
    const expandedStack = parseFloat(styles.getPropertyValue('--expanded-height')) || 0;

    this._cacheZoneFootprints();

    const viewAllFootprint = parseFloat(getComputedStyle(this._zone).getPropertyValue('--view-all-footprint'))
      || this._viewAllWrap.offsetHeight;
    const expandedPadding = parseFloat(getComputedStyle(this._zone).getPropertyValue('--zone-expanded-padding'))
      || (this._zone.classList.contains('bundle-stack-zone--expanded')
        ? parseFloat(getComputedStyle(this._zone).paddingBottom) || 0
        : 0);

    this._zone.style.setProperty('--zone-collapsed-height', `${collapsedStack + viewAllFootprint}px`);
    this._zone.style.setProperty('--zone-expanded-height', `${expandedStack + expandedPadding}px`);
  }

  _zoneTargetHeight(expanded) {
    if (!this._zone) return 0;
    const varName = expanded ? '--zone-expanded-height' : '--zone-collapsed-height';
    return parseFloat(getComputedStyle(this._zone).getPropertyValue(varName)) || 0;
  }

  _syncZoneExpandedClass() {
    if (!this._zone) return;
    const expanded = this.classList.contains('is-expanded')
      || this.classList.contains('is-expanding');
    this._zone.classList.toggle('bundle-stack-zone--expanded', expanded);
  }

  _onResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(this._scheduleMeasure, 150);
  }

  _onClick(e) {
    const cta = e.target.closest('.bundle-card__cta');
    const topCard = this.cards[0];

    if (this.expanded) {
      if (!cta) {
        e.preventDefault();
      }
      return;
    }

    if (cta && topCard.contains(cta)) {
      if (cta.matches('a.bundle-card__cta[href]')) return;
      e.preventDefault();
      return;
    }

    if (cta) {
      e.preventDefault();
      e.stopPropagation();
    }

    this._expand();
  }

  _refreshExpandedMetrics() {
    if (!this.cards || this.cards.length === 0) return;

    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--bundle-stack-gap')) || 8;

    this.cards.forEach((card) => {
      card.style.top = '';
      card.style.height = '';
      card.style.transform = '';
      card.style.filter = '';
    });
    void this.offsetHeight;

    const expandedHeights = this.cards.map((card) => card.offsetHeight);
    let expandedHeight = 0;

    expandedHeights.forEach((height, i) => {
      expandedHeight += height;
      if (i < expandedHeights.length - 1) expandedHeight += gap;
    });

    this.cards.forEach((card, i) => {
      let expandedTop = 0;
      for (let j = 0; j < i; j += 1) expandedTop += expandedHeights[j] + gap;
      card.style.setProperty('--expanded-top', `${expandedTop}px`);
      card.style.setProperty('--card-expanded-height', `${expandedHeights[i]}px`);
    });

    this.style.setProperty('--expanded-height', `${expandedHeight}px`);
    this._measureZoneHeights();
  }

  _pinZoneHeight() {
    if (!this._zone) return;
    this._zone.classList.add('bundle-stack-zone--animating');
    this._zone.style.height = `${this._zone.offsetHeight}px`;
  }

  _pinStackHeight() {
    if (this._zone) return;
    this.style.height = `${this.offsetHeight}px`;
  }

  _zoneNaturalHeight() {
    if (!this._zone) return 0;
    const pinned = this._zone.style.height;
    this._zone.style.transition = 'none';
    this._zone.style.height = 'auto';
    const natural = this._zone.offsetHeight;
    this._zone.style.height = pinned;
    void this._zone.offsetHeight;
    this._zone.style.transition = '';
    return natural;
  }

  _setZoneTargetHeight(expanded) {
    if (!this._zone) return;
    if (expanded) {
      this._measureZoneHeights();
      const natural = this._zoneNaturalHeight();
      if (natural > 0) this._zone.style.height = `${natural}px`;
      return;
    }
    const height = this._zoneTargetHeight(false);
    if (height > 0) this._zone.style.height = `${height}px`;
  }

  _collapsedCardMotion(index) {
    const scale = this._collapsedCardScale(index);
    const brightness = index === 0 ? 1 : 1 - index * 0.04;
    return {
      transform: `translateY(0px) scale(${scale})`,
      filter: brightness === 1 ? 'none' : `brightness(${brightness})`,
    };
  }

  _pinCardStyles(mode) {
    const topVarName = mode === 'expanded' ? '--expanded-top' : '--collapsed-top';

    this.cards.forEach((card) => {
      const computed = getComputedStyle(card);
      const topFromVar = getComputedStyle(card).getPropertyValue(topVarName).trim();
      card.style.top = topFromVar || computed.top;
      card.style.height = `${card.offsetHeight}px`;
      card.style.transform = computed.transform;
      card.style.filter = computed.filter;
    });
  }

  _setCollapsedCardTargets(shellHeight, peek) {
    this.cards.forEach((card, i) => {
      card.style.top = `${i * peek}px`;
      card.style.height = `${shellHeight}px`;
      if (i === 0) {
        card.style.transform = 'none';
        card.style.filter = 'none';
      } else {
        card.style.transform = `scale(${this._collapsedCardScale(i)})`;
        card.style.filter = `brightness(${1 - i * 0.04})`;
      }
    });
  }

  _setExpandedCardTargets() {
    this.cards.forEach((card) => {
      const computed = getComputedStyle(card);
      const expandedTop = computed.getPropertyValue('--expanded-top').trim();
      const expandedHeight = computed.getPropertyValue('--card-expanded-height').trim();
      if (expandedTop) card.style.top = expandedTop;
      if (expandedHeight) card.style.height = expandedHeight;
      card.style.transform = 'none';
      card.style.filter = 'none';
    });
  }

  _lockZoneToNaturalHeight() {
    if (!this._zone) return;
    const natural = this._zoneNaturalHeight();
    this._zone.classList.remove('bundle-stack-zone--animating');
    this._zone.style.transition = 'none';
    this._zone.style.height = `${natural}px`;
    void this._zone.offsetHeight;
    this._zone.style.transition = '';
  }

  _releaseZoneHeight() {
    if (!this._zone) return;
    this._zone.classList.remove('bundle-stack-zone--animating');
    this._zone.style.transition = 'none';
    this._zone.style.height = '';
    void this._zone.offsetHeight;
    this._zone.style.transition = '';
  }

  _clearAnimationStyles() {
    if (this._zone) {
      this._zone.classList.remove('bundle-stack-zone--animating');
      this._zone.style.height = '';
    }
    this.style.height = '';
    this.cards?.forEach((card) => {
      card.style.transition = '';
      card.style.top = '';
      card.style.height = '';
      card.style.transform = '';
      card.style.filter = '';
    });
  }

  _clearInnerAnimationStyles() {
    this.style.height = '';
    this.cards?.forEach((card) => {
      card.style.transition = '';
      card.style.top = '';
      card.style.height = '';
      card.style.transform = '';
      card.style.filter = '';
    });
  }

  _finalizeExpandLayout() {
    this.classList.remove('is-expanding');
    this._clearInnerAnimationStyles();
    this._measure();
    this._cacheZoneFootprints();
    if (this._zone) this._lockZoneToNaturalHeight();
  }

  _finalizeCollapseLayout() {
    this.classList.remove('is-collapsing');
    this._clearInnerAnimationStyles();
    this._measure();
    this._cacheZoneFootprints();
    this._releaseZoneHeight();
  }

  _stackTransitionMs(kind = 'expand') {
    const varName = kind === 'collapse'
      ? '--bundle-stack-duration-collapse'
      : '--bundle-stack-duration-expand';
    const raw = getComputedStyle(this).getPropertyValue(varName).trim();
    const parsed = parseFloat(raw);
    const fallback = kind === 'collapse' ? 600 : 350;
    return Number.isFinite(parsed) && parsed > 0 ? parsed * 1000 + 100 : fallback + 100;
  }

  _clearCollapseAnimation() {
    this.classList.remove('is-collapsing', 'is-expanding');
    this.removeEventListener('transitionend', this._onCollapseEnd);
    this.removeEventListener('transitionend', this._onExpandEnd);
    if (this._collapseTimer) {
      clearTimeout(this._collapseTimer);
      this._collapseTimer = null;
    }
    if (this._expandTimer) {
      clearTimeout(this._expandTimer);
      this._expandTimer = null;
    }
    this._clearAnimationStyles();
    this._syncZoneExpandedClass();
  }

  _onTransitionEnd(kind, e) {
    if (e.target !== (this._zone ?? this) || e.propertyName !== 'height') return;

    if (kind === 'collapse' && !this.classList.contains('is-collapsing')) return;
    if (kind === 'expand' && !this.classList.contains('is-expanded')) return;

    if (kind === 'collapse') {
      if (this._collapseTimer) {
        clearTimeout(this._collapseTimer);
        this._collapseTimer = null;
      }
      this.removeEventListener('transitionend', this._onCollapseEnd);
    } else {
      if (this._expandTimer) {
        clearTimeout(this._expandTimer);
        this._expandTimer = null;
      }
      this.removeEventListener('transitionend', this._onExpandEnd);
    }

    requestAnimationFrame(() => {
      if (kind === 'expand') {
        this._finalizeExpandLayout();
      } else {
        this._finalizeCollapseLayout();
      }
    });
  }

  _onCollapseEnd(e) {
    this._onTransitionEnd('collapse', e);
  }

  _onExpandEnd(e) {
    this._onTransitionEnd('expand', e);
  }

  _listenForTransitionEnd(kind) {
    const target = this._zone ?? this;
    const handler = kind === 'collapse' ? this._onCollapseEnd : this._onExpandEnd;
    target.addEventListener('transitionend', handler);

    const timerKey = kind === 'collapse' ? '_collapseTimer' : '_expandTimer';
    this[timerKey] = setTimeout(
      () => handler.call(this, { target, propertyName: 'height' }),
      this._stackTransitionMs(kind)
    );
  }

  _beginCollapseAnimation() {
    const { shellHeight, collapsedHeight, peek, expandedHeight } = this._stackVars();

    if (this._zone) {
      this._pinZoneHeight();
    } else {
      this._pinStackHeight();
    }

    const starts = this.cards.map((card, i) => ({
      expandedTop: parseFloat(getComputedStyle(card).top) || 0,
      expandedHeight: card.offsetHeight,
      collapsedTop: i * peek,
    }));

    this.classList.add('is-collapsing');
    this.classList.remove('is-expanded');
    if (this._zone) this._zone.classList.remove('bundle-stack-zone--expanded');

    this.cards.forEach((card, i) => {
      const { expandedTop, expandedHeight: cardHeight, collapsedTop } = starts[i];
      const offsetY = expandedTop - collapsedTop;

      card.style.transition = 'none';
      card.style.top = `${collapsedTop}px`;
      card.style.height = `${cardHeight}px`;
      card.style.transform = offsetY > 0.5 ? `translateY(${offsetY}px) scale(1)` : 'translateY(0px) scale(1)';
      card.style.filter = 'none';
    });

    this.style.height = `${expandedHeight}px`;
    void this.offsetHeight;

    requestAnimationFrame(() => {
      this.cards.forEach((card, i) => {
        card.style.transition = '';
        const motion = this._collapsedCardMotion(i);
        card.style.height = `${shellHeight}px`;
        card.style.transform = motion.transform;
        card.style.filter = motion.filter;
      });

      this.style.height = `${collapsedHeight}px`;
      if (this._zone) this._setZoneTargetHeight(false);
      this._listenForTransitionEnd('collapse');
    });
  }

  _expand() {
    if (this.expanded) return;
    this._clearCollapseAnimation();
    this.expanded = true;

    const { shellHeight, peek } = this._stackVars();

    if (this._zone) {
      this._pinZoneHeight();
    } else {
      this._pinStackHeight();
    }
    this._pinCardStyles('collapsed');

    this.classList.add('is-expanded', 'is-expanding');
    this._syncZoneExpandedClass();
    this._refreshExpandedMetrics();

    const { expandedHeight } = this._stackVars();
    this._setCollapsedCardTargets(shellHeight, peek);
    this.style.height = `${expandedHeight}px`;
    this._cacheZoneFootprints();
    void this.offsetHeight;

    requestAnimationFrame(() => {
      this._setExpandedCardTargets();
      if (this._zone) this._setZoneTargetHeight(true);
      this._listenForTransitionEnd('expand');

      if (this._scrollListenTimer) clearTimeout(this._scrollListenTimer);
      this._scrollListenTimer = setTimeout(() => {
        if (!this.expanded) return;
        this._scrollAtExpand = window.scrollY;
        this._collapseScrollThreshold = this._getCollapseScrollThreshold();
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }, this._stackTransitionMs('expand'));
    });
  }

  _collapse() {
    if (!this.expanded) return;
    window.removeEventListener('scroll', this._onScroll);
    this._clearCollapseAnimation();

    this.expanded = false;
    void this.offsetHeight;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.expanded) return;
        this._beginCollapseAnimation();
      });
    });
  }

  _getCollapseScrollThreshold() {
    const raw = getComputedStyle(this).getPropertyValue('--bundle-stack-collapse-scroll-px').trim();
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 600;
  }

  _onScroll() {
    if (window.scrollY >= this._scrollAtExpand + this._collapseScrollThreshold) {
      this._collapse();
    }
  }
}

if (!customElements.get('bundle-stack')) {
  customElements.define('bundle-stack', BundleStack);
}
